import { from, of, EMPTY } from "rxjs";
import {
  debounceTime,
  mergeMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { combineEpics, ofType } from "redux-observable";
import { api, apiConfig } from "../api/api";
import {
  types,
  switchIsOn,
  addJobService,
  checkSpectrePosLink,
  getJobServices,
  getLocations,
  getSalesPersons,
  spectreposOpenOrder,
  spectreposOrderFailed,
  woocommerceAddToOrderDone,
  woocommerceAddToOrderFromSKUDone,
  woocommerceCheckBallInOpenOrderDone,
  woocommerceCheckSKUExistsDone,
  woocommerceCheckUserExists,
  woocommerceCheckUserExistsDone,
  woocommerceCreateProductDone,
  woocommerceCreateProductFailure,
  woocommerceMatchesFound,
  woocommerceRemoveFromOrderBySKUDone,
  writeSpectrePosLinkDone,
} from "./plugin.redux";
import { types as bowlerTypes } from "../bowlers/bowlers.redux";

/**
 * SpectrePOS for a shop that lives on HBS (Chentro).
 *
 * A WordPress shop is driven from the browser: plugin.epic.js signs calls to
 * WooCommerce with the shop's own consumer key. An HBS shop has no such key.
 * The browser asks Spectre (SpectreposShopController), Spectre signs for the
 * control-plane, and the control-plane relays to the shop.
 *
 * The screens do not know the difference. They dispatch the same actions as
 * always (woocommerceCheckUserExists with its nextAction, checkSpectrePosLink,
 * ...) and read the same state; the epics here answer them when the shop is
 * on HBS, and each WooCommerce epic that those actions would wake steps aside
 * (isHbsShop). The shop prices, taxes and checks stock: this side only says
 * which bowler, which code and how many.
 */

export const isHbsShop = (state) =>
  !!state && !!state.plugin && state.plugin.provisionBackend === "hbs";

const hasPos = (state) =>
  (state.plugin.pluginSubscriptions || []).indexOf("SPECTRE_POS") >= 0;

const hasJobBoard = (state) =>
  (state.plugin.pluginSubscriptions || []).indexOf("JOB_BOARD") >= 0;

/**
 * "Send to SpectrePOS" in Settings. Off means nothing leaves Spectre for the
 * register, whatever kind of shop it is: no bowler is linked, no ball or
 * service is added. The WooCommerce epics ask the same question.
 */
export const posSendEnabled = (state) =>
  switchIsOn(
    state && state.plugin && state.plugin.plugin_settings
      ? state.plugin.plugin_settings.pos_send_from_spectre
      : undefined,
  );

/**
 * What an entry action still owes the screens when sending is off: the
 * "loading" it started has to end, and a job service is still a job service
 * even if no item is made for it in the shop. Null when sending is on.
 */
export const whenPosSendOff = (state, action) => {
  if (posSendEnabled(state)) return null;
  const payload = (action && action.payload) || {};
  switch (action && action.type) {
    case types.woocommerceCheckUserExists:
      return of(woocommerceCheckUserExistsDone({ customerId: null }));
    case types.woocommerceCreateProduct:
      return payload.jobService
        ? of(
            woocommerceCreateProductDone(),
            addJobService({ jobService: payload.jobService }),
          )
        : of(woocommerceCreateProductDone());
    case types.woocommerceCheckSKUExists:
      // Nothing to check against: let the edit through.
      return of(woocommerceCheckSKUExistsDone({ skuExists: true }));
    case types.woocommerceCheckBallInOpenOrder:
      return of(
        woocommerceCheckBallInOpenOrderDone({
          ball_name: payload.ball_name,
          found: false,
          orderId: null,
          ballInOrder: false,
          ballLineItemIds: [],
        }),
      );
    default:
      return EMPTY;
  }
};

const shopGet = (route, params) =>
  api.ajaxSession(route, {
    noWorker: true,
    url: apiConfig.appUrl,
    type: "GET",
    params,
  });

const shopPost = (route, data) =>
  api.ajaxSession(route, {
    noWorker: true,
    url: apiConfig.appUrl,
    type: "POST",
    data,
  });

/** The shop's 4xx answers ride on HTTP 200 (see the controller's answer()). */
const refused = (res) => !res || (res.shop_status || 200) >= 400;

const candidatesOf = (res) =>
  (res && (res.candidates || (res.details && res.details.candidates))) || [];

/** A shop customer in the shape ModalWoocommerceMatchPicker draws. */
const toMatch = (c) => ({
  id: c.id,
  first_name: c.name || c.code || "",
  last_name: "",
  email: c.email || "",
  billing: { phone: c.phone || "" },
});

const linkedActions = (spectreCustomerId, res) => {
  const pos = res && res.extra_values && res.extra_values.pos;
  return pos
    ? [writeSpectrePosLinkDone({ spectreCustomerId, posLink: pos })]
    : [];
};

/**
 * The bowler's open sale, for the badge on the header: how many things are in
 * it is all the badge says. `order: null` takes the badge away, so every
 * answer that carries the sale is passed through here -- paid or emptied at
 * the register, the next answer says so.
 */
const openOrderOf = (spectreCustomerId, res) => {
  const order = res && res.order && res.order.id ? res.order : null;
  const lines = (order && order.lines) || [];
  const units = lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0);
  return spectreposOpenOrder({
    customerId: spectreCustomerId,
    order: order
      ? {
          id: order.id,
          code: order.code || "",
          total: order.total || "",
          items: Math.round(units) || lines.length,
        }
      : null,
  });
};

/** Ask the shop for the bowler's open sale; quiet when it cannot be asked. */
const askOpenOrder = async (spectreCustomerId) => {
  try {
    const res = await shopGet("spectreposShopCustomer", {
      customer_id: spectreCustomerId,
    });
    return refused(res) ? [] : [openOrderOf(spectreCustomerId, res)];
  } catch (e) {
    return [];
  }
};

/**
 * Why nothing was added, as the alert wants it: a text key for the pro shop
 * and the detail (the code, the shop's reason) underneath.
 */
const failure = (key, detail) =>
  spectreposOrderFailed({ key, detail: detail || "" });

const rejection = (rejected) => {
  const first = (rejected || [])[0] || {};
  if (first.reason === "sku_not_found") {
    return failure("spectrepos.not_in_shop_body", first.sku);
  }
  if (first.reason === "out_of_stock") {
    return failure("spectrepos.out_of_stock_body", first.sku);
  }
  return failure(
    "spectrepos.order_failed_body",
    [first.sku, first.reason].filter(Boolean).join(" · "),
  );
};

/**
 * Link the bowler with the shop's customer. One alike links by itself, none
 * creates it, several open the picker (`pending` is what the picker hands
 * back when the pro shop chooses).
 */
const linkCustomer = async ({
  spectreCustomer,
  linkTo,
  createNew,
  overwrite,
  pending,
}) => {
  const res = await shopPost("spectreposShopCustomer", {
    customer_id: spectreCustomer.id,
    ...(linkTo ? { link_to: linkTo } : {}),
    ...(createNew ? { create_new: true } : {}),
    ...(overwrite ? { overwrite: true } : {}),
  });
  if (res && res.status === "candidates") {
    return {
      linked: false,
      actions: [
        woocommerceMatchesFound({
          matches: candidatesOf(res).map(toMatch),
          pendingPayload: pending,
        }),
      ],
    };
  }
  if (refused(res) || res.status !== "linked") {
    return { linked: false, actions: [] };
  }
  return {
    linked: true,
    customerId: res.customer && res.customer.id,
    actions: linkedActions(spectreCustomer.id, res),
  };
};

/** Add to / remove from the bowler's open sale, and say how it went. */
const changeOrder = async ({ payload, add, remove, ballSku }) => {
  const { spectreCustomer } = payload;
  const res = await shopPost("spectreposShopOrder", {
    customer_id: spectreCustomer.id,
    ...(payload.hbsLinkTo ? { link_to: payload.hbsLinkTo } : {}),
    ...(add && add.length ? { add } : {}),
    ...(remove && remove.length ? { remove } : {}),
  });

  if (res && res.code === "customer_ambiguous") {
    return [
      woocommerceMatchesFound({
        matches: candidatesOf(res).map(toMatch),
        pendingPayload: payload,
      }),
    ];
  }
  if (refused(res)) {
    return [
      woocommerceCheckUserExistsDone({ customerId: null }),
      failure(
        "spectrepos.order_failed_body",
        (res && (res.code || res.message)) || "",
      ),
    ];
  }

  const actions = [
    woocommerceCheckUserExistsDone({ customerId: null }),
    ...linkedActions(spectreCustomer.id, res),
  ];
  if ("order" in res) actions.push(openOrderOf(spectreCustomer.id, res));
  if (remove && remove.length) {
    actions.push(woocommerceRemoveFromOrderBySKUDone());
  }
  if (!add || !add.length) return actions;

  const rejected = res.rejected || [];
  const added = res.added || [];
  const ballRejected = ballSku && rejected.some((r) => r.sku === ballSku);
  if (added.length === 0 || ballRejected) {
    const first = ballRejected
      ? rejected.filter((r) => r.sku === ballSku)
      : rejected;
    actions.push(rejection(first));
    return actions;
  }
  actions.push(
    woocommerceAddToOrderFromSKUDone({
      missingInsertSkus: rejected.map((r) => r.sku),
    }),
  );
  actions.push(
    woocommerceAddToOrderDone({
      orderData: { id: res.order && res.order.id },
      openUrl: res.open_url || null,
    }),
  );
  return actions;
};

const runCheckUserExists = async (payload, state) => {
  const { spectreCustomer, nextAction } = payload;
  const done = [woocommerceCheckUserExistsDone({ customerId: null })];
  if (!spectreCustomer || !spectreCustomer.id) return done;

  // "None of these" in the picker, with an order waiting behind it: make the
  // new customer first, then go on with what was asked.
  if (payload.hbsCreateNew) {
    const made = await linkCustomer({ spectreCustomer, createNew: true });
    if (!made.linked) return [...done, ...made.actions];
  }

  const sku = payload.sku || payload.hbsSku || "";
  if (nextAction === "SKU add" || nextAction === "order") {
    if (!sku) {
      return [...done, failure("spectrepos.no_barcode_body")];
    }
    const inserts = (payload.insertSkus || []).map((s) => ({ sku: s }));
    // A job service: make sure the shop has it as an item, with today's name
    // and price, before asking for it. Services made before the shop existed
    // (or edited since) would otherwise be "not in the shop".
    const service = (state.plugin.jobServices || []).find(
      (s) => s && s.sku && s.sku === sku,
    );
    if (service && inserts.length === 0) {
      await shopPost("spectreposShopService", {
        sku,
        name: service.name,
        price: Number(service.price) || 0,
      }).catch(() => null);
    }
    return changeOrder({
      payload,
      add: [{ sku, ...(payload.replaceMode ? { replace: true } : {}) }].concat(
        inserts,
      ),
      // Only a ball carries inserts or a replace; a job service is just a code.
      ballSku: sku,
    });
  }
  if (nextAction === "replace order") {
    const oldSku = payload.hbsOldSku || "";
    if (!sku && !oldSku) return done;
    return changeOrder({
      payload,
      remove: oldSku && oldSku !== sku ? [oldSku] : [],
      add: sku ? [{ sku, replace: true }] : [],
      ballSku: sku,
    });
  }
  if (nextAction === "remove order" || nextAction === "SKU remove") {
    if (!sku) return done;
    return changeOrder({ payload, remove: [sku] });
  }

  // "create", "update" or nothing: the bowler's profile was saved.
  const link = await linkCustomer({
    spectreCustomer,
    linkTo: payload.hbsLinkTo,
    overwrite: nextAction === "update",
    pending: payload,
  });
  return link.linked || link.actions.length
    ? [
        woocommerceCheckUserExistsDone({ customerId: link.customerId || null }),
        ...link.actions,
      ]
    : done;
};

/**
 * The profile form tells the POS about a save before the save itself has
 * reached the server (it is debounced, then a round trip away). The shop is
 * told by Spectre's server, which reads the bowler from its own database, so
 * the word is held here until the save is done (setCustomerDone).
 */
let profileSync = null;
const PROFILE_SYNC_TTL_MS = 60000;

/** Run an async handler and emit the actions it returns; a failure is quiet. */
const emit = (work, onError) =>
  from(
    work().catch((e) => {
      console.warn("[spectrepos-hbs]", e);
      return onError ? onError(e) : [];
    }),
  ).pipe(mergeMap((actions) => of(...(actions || []))));

const checkUserExistsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckUserExists),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      if (!isHbsShop(state)) return EMPTY;
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const next = payload && payload.nextAction;
      const profileSave = !next || next === "create" || next === "update";
      if (profileSave && !payload.hbsLinkTo && !payload.hbsCreateNew) {
        profileSync = { overwrite: next === "update", at: Date.now() };
        return of(woocommerceCheckUserExistsDone({ customerId: null }));
      }
      return emit(
        () => runCheckUserExists(payload || {}, state),
        () => {
          const quiet = woocommerceCheckUserExistsDone({ customerId: null });
          // Only "add this" has a screen waiting to hear it did not happen.
          const orderOp =
            ["SKU add", "order", "replace order"].indexOf(
              payload && payload.nextAction,
            ) >= 0;
          return orderOp
            ? [quiet, failure("spectrepos.shop_unreachable_body")]
            : [quiet];
        },
      );
    }),
  );

// Opening a bowler: make sure the shop knows them. Silent unless several
// customers look alike, in which case the picker asks (link-only).
const checkLinkEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.checkSpectrePosLink),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      if (!isHbsShop(state) || !hasPos(state)) return EMPTY;
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const spectreCustomer = payload && payload.spectreCustomer;
      if (!spectreCustomer || !spectreCustomer.id) return EMPTY;
      return emit(async () => {
        const link = await linkCustomer({
          spectreCustomer,
          pending: { spectreCustomer, mode: "background" },
        });
        if (!link.linked) return link.actions;
        return [...link.actions, ...(await askOpenOrder(spectreCustomer.id))];
      });
    }),
  );

// On a hard refresh the bowler can load before the subscriptions do, and the
// first check bails. When they arrive, check the bowler that is open.
const recheckLinkOnSettingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getPluginSubscriptionsDone),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const sc = state.bowlers && state.bowlers.selectedCustomer;
      if (!isHbsShop(state) || !hasPos(state) || !sc || !sc.id) return EMPTY;
      if (!posSendEnabled(state)) return EMPTY;
      return of(checkSpectrePosLink({ spectreCustomer: sc }));
    }),
  );

// The bowler's profile is now saved: tell the shop (see profileSync).
const profileSavedEpic = (action$, state$) =>
  action$.pipe(
    ofType(bowlerTypes.setCustomerDone),
    withLatestFrom(state$),
    mergeMap(([{ payload }, state]) => {
      const waiting = profileSync;
      if (!waiting || !isHbsShop(state) || !hasPos(state)) return EMPTY;
      if (!posSendEnabled(state)) return EMPTY;
      profileSync = null;
      const bowler = payload && payload.bowler;
      if (!bowler || !bowler.id) return EMPTY;
      if (Date.now() - waiting.at > PROFILE_SYNC_TTL_MS) return EMPTY;
      return emit(async () => {
        const link = await linkCustomer({
          spectreCustomer: bowler,
          overwrite: waiting.overwrite,
          pending: { spectreCustomer: bowler, mode: "background" },
        });
        return link.actions;
      });
    }),
  );

const matchResolvedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceMatchResolved),
    withLatestFrom(state$),
    mergeMap(([incoming, state]) => {
      const { payload } = incoming;
      if (!isHbsShop(state)) return EMPTY;
      const off = whenPosSendOff(state, incoming);
      if (off) return off;
      const { action, wcCustomerId, originalPayload } = payload || {};
      if (!originalPayload || !originalPayload.spectreCustomer) return EMPTY;
      const linkTo = action === "link" ? wcCustomerId : undefined;
      const createNew = action === "create";
      if (!linkTo && !createNew) return EMPTY;

      if (originalPayload.mode === "background") {
        return emit(async () => {
          const link = await linkCustomer({
            spectreCustomer: originalPayload.spectreCustomer,
            linkTo,
            createNew,
          });
          return link.actions;
        });
      }
      // Something was waiting on the choice (an order, a profile save): run
      // it again, now knowing which customer.
      return of(
        woocommerceCheckUserExists({
          ...originalPayload,
          hbsLinkTo: linkTo,
          hbsCreateNew: createNew,
        }),
      );
    }),
  );

const checkBallInOpenOrderEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckBallInOpenOrder),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      if (!isHbsShop(state)) return EMPTY;
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const { ball_name, sku } = payload || {};
      const sc = state.bowlers && state.bowlers.selectedCustomer;
      const notThere = (extra) =>
        woocommerceCheckBallInOpenOrderDone({
          ball_name,
          found: false,
          orderId: null,
          ballInOrder: false,
          ballLineItemIds: [],
          ...(extra || {}),
        });
      // Without a code there is nothing to look for; "Add to Order" itself
      // tells the pro shop the ball has no barcode.
      if (!sku || !sc || !sc.id) return of(notThere());
      return emit(
        async () => {
          const res = await shopGet("spectreposShopCustomer", {
            customer_id: sc.id,
            skus: sku,
          });
          if (refused(res)) return [notThere()];
          if (!res.order) return [notThere(), openOrderOf(sc.id, res)];
          return [
            woocommerceCheckBallInOpenOrderDone({
              ball_name,
              found: true,
              orderId: res.order.id,
              ballInOrder: ((res.contains || {})[sku] || 0) > 0,
              // The shop replaces by code, not by line id.
              ballLineItemIds: [],
            }),
            openOrderOf(sc.id, res),
          ];
        },
        (e) => [notThere({ error: (e && e.message) || "Unknown error" })],
      );
    }),
  );

// A pro shop service (drilling, plug...) becomes an item of the shop with the
// same code, then the service itself is saved as always.
const createProductEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCreateProduct),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      if (!isHbsShop(state)) return EMPTY;
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const { jobService } = payload || {};
      const fail = (message) => [
        woocommerceCreateProductFailure({
          message: message || "Failed to create product",
        }),
      ];
      if (!jobService || !jobService.sku) return of(...fail());
      return emit(
        async () => {
          const res = await shopPost("spectreposShopService", {
            sku: jobService.sku,
            name: jobService.name,
            price: Number(jobService.price) || 0,
          });
          if (refused(res)) return fail(res && (res.message || res.code));
          return [woocommerceCreateProductDone(), addJobService({ jobService })];
        },
        (e) => fail(e && e.message),
      );
    }),
  );

const checkSkuExistsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.woocommerceCheckSKUExists),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      const { payload } = action;
      if (!isHbsShop(state)) return EMPTY;
      const off = whenPosSendOff(state, action);
      if (off) return off;
      const sku = payload && payload.sku;
      if (!sku) return of(woocommerceCheckSKUExistsDone({ skuExists: false }));
      return emit(
        async () => {
          const res = await shopGet("spectreposShopProduct", { sku });
          if (!refused(res) && res.exists) {
            return [woocommerceCheckSKUExistsDone({ skuExists: true })];
          }
          // The service being edited is not in the shop yet: put it there, so
          // the edit is not blocked by a shop that was made after the service.
          const editing = state.plugin.selectedJobService;
          if (editing && editing.name) {
            const made = await shopPost("spectreposShopService", {
              sku,
              name: editing.name,
              price: Number(editing.price) || 0,
            });
            return [woocommerceCheckSKUExistsDone({ skuExists: !refused(made) })];
          }
          return [woocommerceCheckSKUExistsDone({ skuExists: false })];
        },
        () => [woocommerceCheckSKUExistsDone({ skuExists: false })],
      );
    }),
  );

/**
 * Tell the shop whether sales at its register leave jobs on the job board,
 * and which codes are pro shop services (the other direction: register ->
 * Spectre). It depends on three things that all live here: the Job Board
 * plugin, the owner's switch, and the service list. Whenever one of them is
 * (re)loaded or saved, the whole state is sent again; the shop replaces what
 * it had.
 *
 * Not before the subscriptions have loaded once: until then "no Job Board"
 * only means "not known yet", and saying so would turn the shop's jobs off.
 */
let subscriptionsKnown = false;

const pushJobBoardEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      types.getPluginSubscriptionsDone,
      types.setPluginSettings,
      types.setJobServices,
    ),
    tap((action) => {
      if (action.type === types.getPluginSubscriptionsDone) {
        subscriptionsKnown = true;
      }
    }),
    // A settings save is followed by a reload of the same things: once is enough.
    debounceTime(1500),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      if (!subscriptionsKnown || !isHbsShop(state) || !hasPos(state)) {
        return EMPTY;
      }
      return emit(async () => {
        const res = await shopPost("spectreposShopJobBoard", {
          has_job_board: hasJobBoard(state),
        });
        // The first time an account has both plugins, Spectre makes its job
        // services out of what the shop sells: there is a list to load. The
        // reload comes back through here once, and by then nothing is made.
        // Its salespeople and locations follow the shop's every time; when
        // any of them changed, those two lists are loaded again as well.
        if (!res) return [];
        return [
          ...(res.seeded > 0 ? [getJobServices()] : []),
          ...(res.staff > 0 ? [getSalesPersons(), getLocations()] : []),
        ];
      });
    }),
  );

export default combineEpics(
  checkUserExistsEpic,
  checkLinkEpic,
  recheckLinkOnSettingsEpic,
  profileSavedEpic,
  matchResolvedEpic,
  checkBallInOpenOrderEpic,
  createProductEpic,
  checkSkuExistsEpic,
  pushJobBoardEpic,
);
